import { Box, Button, Stack, Typography, theme, IconButton } from '@dtx-company/design-system/src'
import { CarouselContext, CarouselProvider, Slide, Slider } from 'pure-react-carousel'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { FC, forwardRef, useContext, useEffect, useId, useRef, useState } from 'react'
import { GENERATOR_HEADING_ID } from '../../../../LoggedOutCodeGeneratorV2/GeneratorHeading/GeneratorHeading'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button'
import { TestimonialsCarouselData } from '../types'
import { fireHeapEvent } from '@dtx-company/true-common/src/utils/heap'
import { useVisibilityThreshold } from '@dtx-company/shared-components/src/hooks/useVisibilityThreshold'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'

//sectionId is used for navigation
const TestimonialsCarouselInner = forwardRef<
  HTMLDivElement,
  {
    data: TestimonialsCarouselData
    sectionId?: string
  }
>(({ data, sectionId }, ref): JSX.Element => {
  // Get the carousel context
  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)
  const numberOfSlides = data.items?.length || 0
  const sliderId = useId()
  const { t } = useTranslation('logged-out-generator')
  // Subscribe to changes to the current slide index
  useEffect(() => {
    const onChange = (): void => {
      setCurrentSlide(carouselContext.state.currentSlide)
    }
    carouselContext.subscribe(onChange)
    return () => carouselContext.unsubscribe(onChange)
  }, [carouselContext])

  const handleNextSlide = (): void => {
    const nextSlide = currentSlide === numberOfSlides - 1 ? 0 : currentSlide + 1
    carouselContext.setStoreState({ currentSlide: nextSlide })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Next_Slide', {
      next_slide_index: nextSlide,
      header: data.primary?.header
    })
  }

  const handlePrevSlide = (): void => {
    const prevSlide = currentSlide === 0 ? numberOfSlides - 1 : currentSlide - 1
    carouselContext.setStoreState({ currentSlide: prevSlide })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Previous_Slide', {
      prev_slide_index: prevSlide,
      header: data.primary?.header
    })
  }

  const handleGoToSlide = (newIndex: number): void => {
    carouselContext.setStoreState({ currentSlide: newIndex })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Slide_button', {
      index: newIndex,
      header: data.primary?.header
    })
  }

  return (
    <Stack
      spacing={theme.spacing(8)}
      direction="column"
      alignItems="center"
      sx={{
        padddingBottom: {
          md: theme.spacing(8),
          lg: theme.spacing(16)
        }
      }}
      ref={ref}
      id={sectionId}
      data-testid={sectionId}
    >
      <Box sx={{ textAlign: 'center', px: { md: 33.5 }, pb: theme.spacing(5) }}>
        <Typography variant="headlineMedium">{data.primary?.header}</Typography>
      </Box>
      <Box
        sx={theme => ({
          display: 'grid',
          justifyItems: 'center',
          alignItems: 'center',
          mx: 'auto',
          gap: 4,
          maxWidth: { xs: theme.spacing(270.5), md: theme.spacing(298.5) },
          width: '100%',
          px: { xs: 1.5, md: 7.5 },
          gridTemplateColumns: { xs: '4% 92% 4%', lg: `auto ${theme.spacing(258)} auto` }
        })}
      >
        <IconButton
          data-heap-id="TestimonialsCarouselPrevButton"
          onClick={handlePrevSlide}
          variant="text"
          icon={ChevronLeft}
          aria-label={t('LoggedOutGenerator.Testimonials.PreviousSlideButton.ariaLabel')}
        />

        <Slider style={{ width: '100%' }} className="carousel-testid">
          <Box
            sx={{
              backgroundColor: 'surface.1'
            }}
          >
            {data.items?.map((item, index) => {
              const ariaLabelId = `${sliderId}-label-${index}`
              const ariaDescriptionId = `${sliderId}-description-${index}`
              return (
                <Slide
                  index={index}
                  key={index}
                  aria-labelledby={ariaLabelId}
                  aria-describedby={ariaDescriptionId}
                  onFocus={() => {
                    carouselContext.setStoreState({ isPlaying: false })
                  }}
                  onBlur={() => {
                    carouselContext.setStoreState({ isPlaying: true })
                  }}
                >
                  <Box
                    key={item.image.url}
                    sx={{
                      p: { _: 5, md: 6.25 },
                      borderRadius: '12px',
                      backgroundColor: 'secondary.backgroundLight',
                      maxWidth: theme.spacing(258.25),
                      verticalAlign: 'top',
                      display: 'grid',
                      gridTemplateColumns: { _: '1fr', md: 'auto 42%' }
                    }}
                    data-testid="carousel-slide"
                  >
                    <Stack
                      sx={{
                        pl: 8,
                        pt: 12
                      }}
                      direction="column"
                      justifyContent="space-between"
                      spacing={theme.spacing(10)}
                    >
                      <Stack
                        direction="column"
                        spacing={theme.spacing(10)}
                        sx={{ pr: { md: 13 }, p: { whiteSpace: 'pre-wrap' } }}
                      >
                        <Typography variant="headlineSmall" aria-label="Testimonial title">
                          {item.testimonial_title}
                        </Typography>
                        <Typography variant="bodyMedium" component="p">
                          {item.text}
                        </Typography>
                      </Stack>
                      {item.button_label && (
                        <Box>
                          <Button
                            data-heap-id={`TestimonialsCarouselSlide${item.testimonial_title.replace(
                              /\s+/g,
                              ''
                            )}CTAButton`} // Strip all whitespace
                            variant="outlined"
                            href={item.button_href?.url}
                            onClick={() =>
                              fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_slide_button', {
                                index: index,
                                button_label: item.button_label,
                                header: data.primary?.header
                              })
                            }
                          >
                            {item.button_label}
                          </Button>
                        </Box>
                      )}
                    </Stack>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: { _: 'flex-end', md: 'unset' },
                        position: 'relative'
                      }}
                    >
                      {item.image.url && (
                        <Image
                          src={item.image.url}
                          alt={item.image.alt ?? ''}
                          fill
                          style={{ objectFit: 'contain' }}
                          sizes="(max-width: 768px) 254px, (max-pxidth: 1024px) 352px, 416px"
                          loading="lazy"
                        />
                      )}
                    </Box>
                  </Box>
                </Slide>
              )
            })}
          </Box>
        </Slider>
        <IconButton
          data-heap-id="TestimonialsCarouselNextButton"
          variant="text"
          icon={ChevronRight}
          onClick={handleNextSlide}
          aria-label={t('LoggedOutGenerator.Testimonials.NextSlideButton.ariaLabel')}
        />
      </Box>
      <Stack direction={'row'} sx={{ width: '100%', justifyContent: 'center' }}>
        {data.items?.map((_, id) => (
          <GenericButton
            onClick={() => handleGoToSlide(id)}
            key={`slide-${id}`}
            bg={id === currentSlide ? 'primary.flowBlue' : 'primary.white'}
            border="1px solid"
            borderColor="primary.flowBlue"
            mx={1}
            width={theme.spacing(2)}
            height={theme.spacing(2)}
            borderRadius="50%"
            aria-label={t('LoggedOutGenerator.Testimonials.Slide.button.ariaLabel', {
              number: id + 1
            })}
          />
        ))}
      </Stack>
      <Stack sx={{ alignItems: 'center', pb: 22 }}>
        <Button
          target="_self"
          data-heap-id="TestimonialsCarouselCTAButton"
          variant="contained"
          href={`#${GENERATOR_HEADING_ID}`}
          onClick={e => {
            e.preventDefault()
            fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_CTAButton', {
              label: data.primary?.button_label,
              button_variant: data.primary?.button_variant
            })
            window.scroll(0, 0)
          }}
        >
          {data.primary?.button_label}
        </Button>
      </Stack>
    </Stack>
  )
})

TestimonialsCarouselInner.displayName = 'TestimonialsCarousel'

const TestimonialsCarousel: FC<{
  data?: TestimonialsCarouselData
  sectionId?: string
}> = ({ data, sectionId }): JSX.Element | null => {
  const testimonialsContainerRef = useRef<HTMLDivElement | null>(null)
  const { visible } = useVisibilityThreshold({ threshold: 0.1, ref: testimonialsContainerRef })

  if (!data) return null

  return (
    <CarouselProvider
      totalSlides={data.items?.length || 0}
      naturalSlideWidth={500}
      naturalSlideHeight={200}
      isIntrinsicHeight
      infinite
      interval={9000}
      isPlaying={visible}
      dragEnabled={false}
      visibleSlides={1}
    >
      <TestimonialsCarouselInner data={data} sectionId={sectionId} ref={testimonialsContainerRef} />
    </CarouselProvider>
  )
}

export default TestimonialsCarousel
